import Portfolio from "../Portfolios/Entities/Portfolio";
import {Link, useLocation} from "react-router-dom";
import PortfolioThumbnails from "../Components/PortfolioThumbnails";
import React, {useEffect, useRef} from "react";
import ClearLine from "../Components/ClearLine";
import {LeftColumn, RightColumn} from "../Components/Column";
import Page from "./Page";
import PortfolioImage from "../Portfolios/Entities/PortfolioImage";

function PortfolioPage({
                           portfolios,
                           selectedPortfolioPiece,
                           setSelectedPortfolioPiece
                       }: {
                            portfolios: Portfolio[]
                            selectedPortfolioPiece: PortfolioImage | null,
                            setSelectedPortfolioPiece: (piece: PortfolioImage) => void
                        }) {
    const location = useLocation().pathname;

    const portfolioPath = location
        .replace("/portfolio/", "")
        .replace("/statement", "");

    const viewingStatement = location.endsWith("/statement");
    const prevPathRef = useRef<string | null>(null);

    let portfolio: Portfolio | null = null;
    let nextPortfolio: Portfolio | null = null;

    // Loop through the portfolios to find the index with this path
    for (let i = 0; i < portfolios.length; i++) {
        if (portfolios[i].path === portfolioPath) {
            portfolio = portfolios[i];

            // Find the next portfolio for the link to the next one
            if (i + 1 < portfolios.length) {
                nextPortfolio = portfolios[i + 1];
            }

            break;
        }
    }

    // If we weren't able to find the portfolio, just default to the home page.
    if (!portfolio) {
        portfolio = portfolios[0];
        nextPortfolio = portfolios.length > 1 ? portfolios[1] : null;
    }

    nextPortfolio ??= portfolios.length > 1 ? portfolios[0] : null;

    if (!portfolio) {
        throw new Error("No portfolio found.");
    }

    const pieces = portfolio.pieces;

    // Use useEffect to handle state updates and prevent infinite re-renders
    useEffect(() => {
        // Only update if the portfolio path has changed
        if (prevPathRef.current !== portfolioPath) {
            const currentPortfolio = portfolios.find(p => p.path === portfolioPath) || portfolios[0];
            // Only set if the current selected piece is not in this portfolio (compare by image property)
            const pieceExistsInPortfolio = selectedPortfolioPiece &&
                currentPortfolio.pieces.some(p => p.image === selectedPortfolioPiece.image);

            if (!pieceExistsInPortfolio) {
                setSelectedPortfolioPiece(currentPortfolio.pieces[0]);
            }
            prevPathRef.current = portfolioPath;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioPath]);

    // Use the first piece as fallback for rendering if null
    const displayPiece = selectedPortfolioPiece ?? pieces[0];

    // Last page loops around back to Next Portfolio » New Work which is the first one.
    // Figure out the next page
    const nextPageTitle = nextPortfolio?.name ?? "New Work";
    const nextPageUrl = nextPortfolio?.path ?? portfolios[0].path;

    const portfolioContent = (
        <>
            <PortfolioThumbnails
                portfolio={pieces}
                piecesColumns={portfolio.piecesColumns}
                tallMode={portfolio.piecesTallMode ?? false}
                selectedPortfolioPiece={displayPiece}
                setSelectedPortfolioPiece={setSelectedPortfolioPiece}/>

            {portfolio.piecesNote ? (
                <p style={{clear: "both", whiteSpace: 'pre-line'}}>{portfolio.piecesNote}</p>) : null}

            <div id="portFoot">
                <Link to={"/portfolio/" + portfolio.path + "/statement"} className="portFoot">Statement</Link> &raquo;
            </div>

            <div id="portFoot">
                Next Portfolio &raquo; <Link to={"/portfolio/" + nextPageUrl}
                                             className="portFoot">{nextPageTitle}</Link>
            </div>
        </>
    );

    const statementContent = (
        <div style={{whiteSpace: 'pre-line'}}>
            <LeftColumn>
                {portfolio.statement.leftColumn.map((s, i) => (<p key={i}>{s}</p>))}
            </LeftColumn>
            <RightColumn>
                {portfolio.statement.rightColumn.map((s, i) => (<p key={i}>{s}</p>))}
            </RightColumn>
            <ClearLine/>
            <div id="portFoot">
                <Link to={"/portfolio/" + portfolio.path} className="portFoot">Return to Portfolio</Link> »
            </div>
        </div>
    );

    const content = viewingStatement ? statementContent : portfolioContent;

    const statementTitle = viewingStatement ? (portfolio.statementTitle ?? portfolio.name) + " : Statement" : portfolio.name;

    return (
        <Page title={`Portfolio : ${statementTitle}`} image={displayPiece} portfolio={portfolio}>
            {content}
        </Page>
    )
}

export default PortfolioPage;